/**
 * KeyboardInput.js - Keyboard Input Handler
 *
 * Handles keyboard input events and state tracking.
 * Provides:
 * - Key state tracking (pressed/released)
 * - Key event callbacks
 * - Focus/blur handling
 */

import { ClientLogger } from '../logger/ClientLogger.js';

const keyboardLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const keyboardLogger = ClientLogger.getLogger('input') || keyboardLoggerFallback;

const KeyboardInput = {
  // Key state map
  keys: {},

  // Callbacks for key events
  callbacks: {
    onKeyDown: null,
    onKeyUp: null
  },

  // Reference to game state for ability checks
  gameState: null,

  /**
   * Initialize keyboard input handling
   * @param {Object} options - Configuration options
   * @param {GameState} options.gameState - Game state reference
   * @param {Function} options.onKeyDown - Key down callback
   * @param {Function} options.onKeyUp - Key up callback
   */
  init(options = {}) {
    this.gameState = options.gameState || null;
    this.callbacks.onKeyDown = options.onKeyDown || null;
    this.callbacks.onKeyUp = options.onKeyUp || null;

    // Bind event listeners
    // Use capture phase (true) for Tab key to intercept it before browser handles it
    // This is especially important for Edge which may intercept Tab at a higher level
    window.addEventListener('keydown', (e) => {
      // Special handling for TAB - use capture phase to catch before browser
      if (e.key === 'Tab' || e.code === 'Tab') {
        e.preventDefault();
        this.handleKeyDown(e);
        return false;
      }
    }, true); // true = capture phase

    document.addEventListener('keydown', (e) => {
      keyboardLogger.debug('[KeyboardInput] Raw keydown event:', { key: e.key, code: e.code, target: e.target.tagName });

      // Tab is handled in capture phase above, skip it here
      if (e.key === 'Tab' || e.code === 'Tab') {
        return;
      }

      // Ignore keyboard input if typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      this.handleKeyDown(e);
    });
    document.addEventListener('keyup', (e) => this.handleKeyUp(e));

    // Handle focus/blur
    window.addEventListener('blur', () => this.clearAllKeys());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.clearAllKeys();
      }
    });
  },

  /**
   * Handle keydown event
   * @param {KeyboardEvent} e - Keyboard event
   */
  handleKeyDown(e) {
    const key = e.key.toLowerCase();
    const code = e.code; // Use code for keys like ShiftLeft, ShiftRight, etc.

    keyboardLogger.debug('[KeyboardInput] Key pressed:', { key, code });

    // Prevent default for game keys to avoid browser shortcuts
    if (key === 'shift' || code === 'ShiftLeft' || code === 'ShiftRight' || 
        key === 'q' || key === 'e' || key === 'r' || key === 'tab' || code === 'Tab' ||
        key === ' ' || code === 'Space' ||
        ['1','2','3','4','5'].includes(key)) {
      e.preventDefault();
    }

    // Set key state for both key and code
    this.keys[key] = true;
    if (code) {
      this.keys[code] = true;
    }

    // Call callback if registered
    if (this.callbacks.onKeyDown) {
      this.callbacks.onKeyDown(key, e, code);
    }
  },

  /**
   * Handle keyup event
   * @param {KeyboardEvent} e - Keyboard event
   */
  handleKeyUp(e) {
    const key = e.key.toLowerCase();
    const code = e.code;

    // Clear key state for both key and code
    this.keys[key] = false;
    if (code) {
      this.keys[code] = false;
    }

    // Call callback if registered
    if (this.callbacks.onKeyUp) {
      this.callbacks.onKeyUp(key, e, code);
    }
  },

  /**
   * Check if a key is currently pressed
   * @param {string} key - Key name (lowercase)
   * @returns {boolean}
   */
  isKeyPressed(key) {
    return this.keys[key.toLowerCase()] || false;
  },

  /**
   * Check if any of the given keys are pressed
   * @param {string[]} keyList - Array of key names
   * @returns {boolean}
   */
  isAnyKeyPressed(keyList) {
    return keyList.some(key => this.isKeyPressed(key));
  },

  /**
   * Get movement input from WASD/Arrow keys
   * @returns {{up: boolean, down: boolean, left: boolean, right: boolean}}
   */
  getMovementInput() {
    return {
      up: this.isKeyPressed('w') || this.isKeyPressed('arrowup'),
      down: this.isKeyPressed('s') || this.isKeyPressed('arrowdown'),
      left: this.isKeyPressed('a') || this.isKeyPressed('arrowleft'),
      right: this.isKeyPressed('d') || this.isKeyPressed('arrowright')
    };
  },

  /**
   * Check if reload key is pressed
   * @returns {boolean}
   */
  isReloadPressed() {
    return this.isKeyPressed('r');
  },

  /**
   * Drift (hold-to-slide) key: space bar.
   * @returns {boolean}
   */
  isDriftPressed() {
    return this.isKeyPressed(' ') || this.isKeyPressed('Space');
  },

  /**
   * Clear all key states
   * Called when window loses focus
   */
  clearAllKeys() {
    for (const key in this.keys) {
      this.keys[key] = false;
    }
  },

  /**
   * Get the raw keys object (for backward compatibility)
   * @returns {Object}
   */
  getKeys() {
    return this.keys;
  }
};

// Export for use in other modules
// For now, KeyboardInput is a global object loaded via script tag

export { keyboardLoggerFallback, keyboardLogger, KeyboardInput };
